import { useState } from 'react';

import { useOneOffSubscription } from 'helpers/action_cable/use_one_off_subscription';

import type { StreamFrame } from '../types';
import type { ToolCall } from '../tools/types';

export interface TurnResult {
  toolCalls?: ToolCall[];
  error?: boolean;
}

// Return false when no frames are expected
type Start = (requestId: string) => Promise<boolean>;

const isTerminal = (frame: StreamFrame) => frame.type === 'done' || frame.type === 'error';

// Subscribe before the request that feeds it, so no frame can land before there is something listening,
// then stream until a terminal one arrives
export function useTurnSubscription() {
  const { subscribe, unsubscribe } = useOneOffSubscription();
  const [loading, setLoading] = useState(false);

  function run(start: Start, onFrame: (frame: StreamFrame) => void): Promise<TurnResult> {
    return new Promise<TurnResult>((resolve) => {
      setLoading(true);

      function finish(result: TurnResult) {
        setLoading(false);
        resolve(result);
      }

      subscribe<StreamFrame>(
        {
          async connected(requestId: string) {
            if (await start(requestId)) {
              return;
            }

            unsubscribe();
            finish({});
          },

          connectedError() {
            onFrame({ type: 'error', error: true });
            finish({ error: true });
          },

          received(frame) {
            onFrame(frame);
            if (frame.type === 'done') {
              finish({ toolCalls: frame.toolCalls });
            } else if (frame.type === 'error') {
              finish({ error: true });
            }
          },
        },
        { isTerminal }
      );
    });
  }

  return { loading, run };
}
